import React, { ReactElement, useMemo } from 'react';
import { isEmpty } from 'b2b-apps/shop/common/utils/empty/empty';
import { config } from 'b2b-apps/shop/config';
import { useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { BulletPoints } from 'b2b-apps/shop/product/components/ProductData/BulletPoints';
import { Price } from 'b2b-apps/shop/product/components/ProductData/PriceList/Price';
import {
  ProductDescription,
} from 'b2b-apps/shop/product/components/ProductDetails/ProductDescription/ProductDescription';
import { ProductSpecification } from 'b2b-apps/shop/product/components/ProductDetails/ProductSpecification';
import { useProductGalleryEffect } from 'b2b-apps/shop/product/hooks';
import { FIELD_NAME } from '../PDFForm/withContainer';
import { ComponentType, GalleryImagesType, ImageType, Props } from './PDFContent.types';
import './PDFContent.scss';

const PDFContent: ComponentType = ({
  values,
  product,
  text,
}: Props): ReactElement => {
  const t = useTranslate();
  const { description, effectivePricing, manufacturer } = product;
  const { bulletPoints, title } = description;
  const cdnUrl = config.url.cdn.images;
  const gallery = useProductGalleryEffect(product.sku);
  const { isLoaded: isGalleryLoaded, data: galleryData } = gallery;
  const isDescriptionAvailable = description
    && (description.subtitle !== ''
      || description.lead !== ''
      || (Array.isArray(description.dangerDetails)
        && description.dangerDetails.length > 0)
      || (Array.isArray(description.dangerIcons)
        && description.dangerIcons.length > 0)
      || (Array.isArray(description.paragraphs)
        && description.paragraphs.length > 0)
      || (Array.isArray(description.banners) && description.banners.length > 0)
      || (Array.isArray(description.remarks) && description.remarks.length > 0));

  const galleryImages = useMemo(
    (): GalleryImagesType | null => {
      if (!isGalleryLoaded || !galleryData || isEmpty(galleryData.images)) {
        return null;
      }

      const fullGallery = galleryData.images.map((img) => {
        const imgUrl = img.sizes.preview || img.sizes.full || img.sizes.thumbnail || '';

        return {
          src: `${cdnUrl}${imgUrl}`,
        };
      });

      if (fullGallery.length > 1) {
        const [main, ...rest] = fullGallery;
        const additional = rest.length < 6 ? rest : rest.filter((el, index) => index < 5);

        return {
          main,
          additional,
        };
      }

      return {
        main: fullGallery[0],
        additional: [],
      };
    },
    [cdnUrl, galleryData, isGalleryLoaded],
  );

  const renderPriceList = () => {
    let customPrice = {};
    let fixedPrice = {};

    if (values.offer) {
      customPrice = {
        value: values.offerPriceValue,
        tax: values.offerValue.includes(FIELD_NAME.withVAT)
          ? t('<strong>incl.</strong> {taxRate} VAT', {
            taxRate: `${effectivePricing.priceTaxRate}%`,
          })
          : t('<strong>excl.</strong> {taxRate} VAT', {
            taxRate: `${effectivePricing.priceTaxRate}%`,
          }),
      };
    }

    if (values.fixedPrices) {
      if (values.fixedPricesTaxValue.includes(FIELD_NAME.listPrice)) {
        fixedPrice = {
          value: effectivePricing.listPrice,
          tax: text?.listPrice || t(
            'SRP <strong>incl.</strong> {taxRate} VAT',
            {
              taxRate: `${effectivePricing.priceTaxRate}%`,
            },
            'Suggested Retail Price with VAT',
          ),
        };
      }

      if (values.fixedPricesTaxValue.includes(FIELD_NAME.listPriceWithoutVat)) {
        fixedPrice = {
          value: effectivePricing.listPriceWithoutVat,
          tax: text?.listPriceWithoutVat || t(
            'SRP <strong>excl.</strong> {taxRate} VAT',
            {
              taxRate: `${effectivePricing.priceTaxRate}%`,
            },
            'Suggested Retail Price Without VAT',
          ),
        };
      }

      if (values.fixedPricesTaxValue.includes(FIELD_NAME.userPrice)) {
        fixedPrice = {
          value: effectivePricing.userPrice,
          tax: t(
            'PP <strong>incl.</strong> {taxRate} VAT',
            {
              taxRate: `${effectivePricing.priceTaxRate}%`,
            },
            'Purchase Price',
          ),
        };
      }

      if (values.fixedPricesTaxValue.includes(FIELD_NAME.userPriceWithoutVat)) {
        fixedPrice = {
          value: effectivePricing.userPriceWithoutVat,
          tax: t(
            'PP <strong>excl.</strong> {taxRate} VAT',
            {
              taxRate: `${effectivePricing.priceTaxRate}%`,
            },
            'Purchase Price without VAT',
          ),
        };
      }
    }

    const mainPrice: any = values.offer ? customPrice : fixedPrice;
    const secondaryPrice: any = values.offer && values.fixedPrices ? fixedPrice : undefined;
    const bothPricesHidden = !values.offer && !values.fixedPrices;

    if (bothPricesHidden) {
      return null;
    }

    return (
      <section className={'PDFContent-PriceBox'}>
        <div className="PDFContent-PriceBox--PrimaryWrapper">
          <Price
            price={mainPrice.value}
            tax={mainPrice.tax || ''}
            isSecondary={false}
          />
        </div>
        {secondaryPrice && (
          <div className="PDFContent-PriceBox--SecondaryWrapper">
            <Price
              className="PDFContent-PriceBox--Secondary"
              price={secondaryPrice.value}
              tax={secondaryPrice.tax || ''}
              isSecondary={true}
            />
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="PDFContent">
      <div className="PDFContent-Body ProductContent">
        <section className="PDFContent-ContentColumn--Left">
          {values.productName && <h1 className="ProductData-Title">{title}</h1>}
          {renderPriceList()}
          {values.shortInfo && <BulletPoints bulletPoints={bulletPoints} />}

          <table className="ProductData-Features cds-Table cds-Table--Features">
            <tbody>
              {values.sku && (
                <tr>
                  <td>{t('SKU')}:</td>
                  <td>{product.sku}</td>
                </tr>
              )}
              {values.manufacturerNumber && (
                <tr>
                  <td>{t('Manufacturer Product ID')}:</td>
                  <td>{product.manufacturer.productId}</td>
                </tr>
              )}
              {values.warrantyInfo && (
                <tr>
                  <td>{t('Manufacturer warranty')}:</td>
                  <td className="PDFContent-WarrantyInfo">
                    {manufacturer.warranty
                      ? t(['month', '{n} months', 'n'], {
                        n: manufacturer.warranty,
                      })
                      : t('0 months')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {!!values.additionalComments.length && (
            <p>{values.additionalComments}</p>
          )}
        </section>
        <section className="PDFContent-ContentColumn--Right PDFContent-Gallery">
          {values.mainImage && galleryImages && (
            <div className="PDFContent-ImageWrapper">
              <img
                src={galleryImages.main?.src}
                alt={title}
                className="PDFContent-Image"
              />
            </div>
          )}
          {values.additionalImages && galleryImages
            ? !!galleryImages.additional.length
              && galleryImages.additional.map((img: ImageType, index: number) => (
                <div key={index} className="PDFContent-ImageWrapper">
                  <img src={img.src} alt={title} className="PDFContent-Image" />
                </div>
              ))
            : null}
        </section>
        {values.description && isDescriptionAvailable && (
          <section className="PDFContent-ContentColumn--Left">
            <div className="PDFContent-ProductDataTile">
              <div className="PDFContent-ProductDataTileTitle">
                {t('Description')}
              </div>
              <ProductDescription description={product.description} />
            </div>
          </section>
        )}

        {values.specification && (
          <section className="PDFContent-ContentColumn--Left">
            <div className="PDFContent-ProductDataTile">
              <div className="PDFContent-ProductDataTileTitle">
                {t('Specification')}
              </div>
              <ProductSpecification
                specification={product.description.specification}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export { PDFContent };
